import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { FileText, Search, Plus, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { templatesAPI } from '../../services/api';

const TemplatesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Fetch templates
  const { data: templatesData, isLoading, error } = useQuery(
    ['user-templates', selectedCategory, searchTerm],
    () => templatesAPI.getAll({ 
      category: selectedCategory === 'all' ? undefined : selectedCategory,
      search: searchTerm || undefined,
      isActive: true,
      limit: 50 
    }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        console.log('User templates data:', data);
      },
      onError: (error) => {
        console.error('User templates error:', error);
      }
    }
  );

  // Fetch categories
  const { data: categoriesData } = useQuery(
    'template-categories',
    () => templatesAPI.getCategories(),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Handle different possible response structures
  const templates = templatesData?.data?.data?.templates || templatesData?.data?.templates || [];
  const categories = categoriesData?.data?.data?.categories || categoriesData?.data?.categories || [];
  
  // Debug logging
  console.log('User templates count:', templates.length);
  console.log('User categories:', categories);
  console.log('User templates data structure:', templatesData);
  console.log('User error:', error);

  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (template.template_text || template.templateText || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Templates</h1>
          <p className="mt-2 text-sm text-gray-700">
            Browse and use predefined announcement templates for your submissions.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2 flex">
          <button 
            onClick={async () => {
              try {
                const result = await templatesAPI.getAll({ limit: 5 });
                console.log('Direct user API test result:', result);
                alert(`API test: Found ${result.data?.templates?.length || 0} templates`);
              } catch (error) {
                console.error('Direct user API test error:', error);
                alert(`API test failed: ${error.message}`);
              }
            }}
            className="btn btn-outline"
          >
            Test API
          </button>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                selectedCategory === 'all'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              All ({templates.length})
            </button>
            {categories.map((category) => {
              const categoryCount = templates.filter(t => t.category === category).length;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    selectedCategory === category
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {category} ({categoryCount})
                </button>
              );
            })}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="px-4 py-5 sm:p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchTerm ? 'No templates found' : 'No templates available'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm 
                  ? `No templates match "${searchTerm}". Try a different search term.`
                  : 'There are no templates available in this category.'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredTemplates.map((template) => (
                <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {template.name}
                      </h3>
                      <p className="text-sm text-primary-600 mt-1">
                        {template.category}
                      </p>
                      {template.description && (
                        <p className="text-sm text-gray-500 mt-2">
                          {template.description}
                        </p>
                      )}
                      <div className="mt-3">
                        <p className="text-xs text-gray-400 line-clamp-3">
                          {(template.template_text || template.templateText || '').substring(0, 150)}
                          {(template.template_text || template.templateText || '').length > 150 ? '...' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      {template.placeholders?.length || 0} placeholders
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          // Preview template
                          alert(`Template: ${template.name}\n\n${template.template_text || template.templateText}`);
                        }}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </button>
                      <Link
                        to={`/submissions/create?template=${template.id}`}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Use Template
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
